/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { localize } from '@saviynt/common';
import {
  Box,
  Checkbox,
  Icon,
  Link,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';

import { MANDATORY_COLUMNS } from './CertsExportsUtils';
import { invertedMessages } from './messages';

function SortableItem({ option, renderCheckboxes }) {
  const l = useInvertedLocalize(invertedMessages);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const constrainedTransform = transform
    ? {
        ...transform,
        x: 0,
      }
    : transform;

  const style = {
    transform: CSS.Transform.toString(constrainedTransform),
    transition,
    position: 'relative',
    width: '100%',
    ...(isDragging
      ? {
          zIndex: 1,
          boxSizing: 'border-box',
          maxWidth: '100%',
        }
      : {}),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='ColumnsContainer-options'
      key={option?.value}>
      {renderCheckboxes(option)}
      <div {...attributes} {...listeners} className='ColumnsContainer-drag'>
        {!isDragging && (
          <Tooltip
            trigger={
              <div className='ColumnsContainer-dragIcon'>
                <Icon
                  kind='DragVertical'
                  color='neutral-1000'
                  size='smallMedium'
                  data-testid='icon-DragVertical'
                />
              </div>
            }
            text={l('Drag to Reorder')}
            placement='bottom'
          />
        )}
        {isDragging && (
          <div className='ColumnsContainer-dragIcon'>
            <Icon kind='DragVertical' color='neutral-1000' size='smallMedium' />
          </div>
        )}
      </div>
    </div>
  );
}

function IncludedInExport({ allColumnsData, setAllColumnsData, selectedTab }) {
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const selectedTabColumns = allColumnsData[selectedTab];
  const includedColumns = selectedTabColumns?.filter((item) => item.isChecked);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChange = (id) => {
    const updatedColumns = selectedTabColumns?.map((item) =>
      item.id === id ? { ...item, isChecked: false } : item
    );

    setAllColumnsData({
      ...allColumnsData,
      [selectedTab]: updatedColumns,
    });
  };

  const reorderColumnsData = (activeId, overId) => {
    const oldIndex = selectedTabColumns.findIndex(
      (item) => item.id === activeId
    );
    const newIndex = selectedTabColumns.findIndex((item) => item.id === overId);

    const reorderedColumns = arrayMove(selectedTabColumns, oldIndex, newIndex);
    const sortedColumns = reorderedColumns.sort(
      (a, b) => b.isChecked - a.isChecked
    );

    setAllColumnsData({
      ...allColumnsData,
      [selectedTab]: sortedColumns,
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    reorderColumnsData(active.id, over.id);
  };

  const removeAll = () => {
    const updatedColumns = selectedTabColumns?.map((item) => ({
      ...item,
      isChecked: !!item.isDisabled,
    }));

    setAllColumnsData({
      ...allColumnsData,
      [selectedTab]: updatedColumns,
    });
  };

  const renderCheckboxes = (option) => (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control, jsx-a11y/no-noninteractive-element-interactions
    <label
      className='ColumnsContainer-checkboxes'
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleChange(option?.id);
        }
      }}>
      <Checkbox
        data-testid={option.id}
        key={option.id}
        id={option.id}
        name={option?.label}
        isChecked={option?.isChecked}
        onChange={() => handleChange(option?.id)}
        showHoverEffect
        isDisabled={option?.isDisabled}
        dataTestId={option?.id}
        className='IncludedInExportContainer-checkbox'
        classNames={{
          inputContainerClassName:
            'IncludedInExportContainer-checkbox--container',
          inputClassName: null,
          labelClassName: null,
        }}
      />
      <Typography className='Certs-IncludedInExport-label' kind='body2'>
        {option?.label}
      </Typography>
    </label>
  );

  const renderMandatoryColumns = (option) => (
    <div className='ColumnsContainer-options' key={option?.value}>
      {renderCheckboxes(option)}
    </div>
  );

  const title =
    messages.CERTS_EXPORT_INCLUDED_IN_EXPORT ||
    localize(intl, getMessageKey('CERTS_EXPORT_INCLUDED_IN_EXPORT'));

  const removeAllText =
    messages.CERTS_EXPORT_REMOVE_ALL ||
    localize(intl, getMessageKey('CERTS_EXPORT_REMOVE_ALL'));

  return (
    <Box>
      <Box className='ColumnsHeader'>
        <Typography kind='body2'>{title}</Typography>
        <Link
          text={removeAllText}
          kind='button'
          onClick={() => removeAll()}
          isDisabled={includedColumns?.length === 1}
          dataTestId='remove-all'
        />
      </Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}>
        <div className='IncludedInExportContainer'>
          <SortableContext
            items={includedColumns?.map((item) => item.id) || []}
            strategy={verticalListSortingStrategy}>
            {includedColumns?.map((option) =>
              MANDATORY_COLUMNS.includes(option?.id?.split('.')[0]) ? (
                renderMandatoryColumns(option)
              ) : (
                <SortableItem
                  key={option.id}
                  option={option}
                  renderCheckboxes={renderCheckboxes}
                />
              )
            )}
          </SortableContext>
        </div>
      </DndContext>
    </Box>
  );
}

IncludedInExport.propTypes = {
  allColumnsData: PropTypes.shape({}).isRequired,
  setAllColumnsData: PropTypes.func.isRequired,
  selectedTab: PropTypes.string.isRequired,
};

SortableItem.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.string,
    label: PropTypes.string,
    isChecked: PropTypes.bool,
    isDisabled: PropTypes.bool,
  }).isRequired,
  renderCheckboxes: PropTypes.func.isRequired,
};

export default IncludedInExport;
